import { useEffect, useRef } from "react";
import { toBisacCodePrefix } from "components/BisacCodes/toBisacCodePrefix";
import { useAnalytics } from "contexts/AnalyticsContext";
import type { BisacFacetItem } from "../bisacFilterItems";

interface UseBisacFilterAnalyticsProps {
  selectedCount: number;
  searchText: string;
  isListPending: boolean;
  items: BisacFacetItem[];
}

export const useBisacFilterAnalytics = ({
  selectedCount,
  searchText,
  isListPending,
  items,
}: UseBisacFilterAnalyticsProps) => {
  const { gaEvent } = useAnalytics();

  // The filter mounts each time its section expands.
  const loggedExpandRef = useRef(false);
  useEffect(() => {
    if (loggedExpandRef.current) {
      return;
    }

    loggedExpandRef.current = true;
    gaEvent({
      type: "expand_bisac_filter",
      payload: { selected_codes_count: selectedCount },
    });
  }, [gaEvent, selectedCount]);

  // Once per settled search text; a refetch for other filter changes is not a new search.
  const loggedSearchTextRef = useRef("");
  useEffect(() => {
    if (!searchText) {
      loggedSearchTextRef.current = "";
      return;
    }

    if (isListPending || loggedSearchTextRef.current === searchText) {
      return;
    }

    loggedSearchTextRef.current = searchText;
    const codePrefix = toBisacCodePrefix(searchText);
    const isCodeMatch =
      Boolean(codePrefix) &&
      items.some(({ value }) => value.startsWith(codePrefix));
    gaEvent({
      type: "search_bisac_filter",
      payload: {
        query: searchText,
        match_type: isCodeMatch ? "code" : "heading",
        results_count: items.length,
      },
    });
  }, [gaEvent, searchText, isListPending, items]);

  return {
    logBrowseOpened: () =>
      gaEvent({
        type: "open_bisac_browse_dialog",
        payload: { selected_codes_count: selectedCount },
      }),
    logCodeToggled: (code: string, checked: boolean) =>
      gaEvent({
        type: "select_bisac_filter_code",
        payload: { code, action: checked ? "select" : "deselect" },
      }),
    logBulkSelection: (
      action: "select_all" | "deselect_all",
      codesCount: number,
    ) =>
      gaEvent({
        type: "bulk_select_bisac_filter_codes",
        payload: { action, codes_count: codesCount },
      }),
  };
};
